import { useState } from 'react';
import { Lock, Sparkles, ArrowRight, ArrowLeft, Loader, Boxes } from 'lucide-react';
import { useAuth } from '../AuthContext';

const LoginScreen = () => {
  const { login } = useAuth();
  // 'choose' = tile launcher · 'login' = passcode entry
  const [mode, setMode] = useState('choose');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const openLogin = () => setMode('login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setBusy(true);
    setError('');
    try {
      await login(passcode);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Login failed');
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{
        backgroundColor: '#F6F4F0',
        backgroundImage:
          'radial-gradient(60rem 40rem at 0% -10%, rgba(31,61,43,0.08), transparent 60%),' +
          ' radial-gradient(50rem 35rem at 100% 110%, rgba(217,119,54,0.07), transparent 65%)',
      }}
    >
      {/* Brand logo */}
      <img
        src="/logo.png"
        alt="Healthy Life Vitamins"
        className="w-56 sm:w-64 h-auto object-contain mb-3 drop-shadow-sm"
      />
      <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#1f3d2b]/70 mb-10 flex items-center gap-3">
        <span className="block w-6 h-px bg-[#1f3d2b]/30" />
        Wellness Club
        <span className="block w-6 h-px bg-[#1f3d2b]/30" />
      </div>

      {mode === 'choose' && (
        <div
          data-testid="launcher-tiles"
          className="w-full max-w-5xl space-y-5 md:space-y-6"
        >
          {/* Wellness Suite hero tile (public booking) — GREEN */}
          <a
            data-testid="launcher-booking"
            href="/booking"
            className="relative block rounded-3xl overflow-hidden no-underline hover:shadow-2xl active:scale-[0.99] transition-all"
            style={{
              minHeight: 220,
              background: 'linear-gradient(135deg, #062d17 0%, #114d2a 100%)',
              boxShadow:
                '0 14px 40px rgba(6,45,23,0.45), inset 0 0 0 1.5px rgba(255,153,0,0.7), inset 0 0 14px rgba(255,153,0,0.25)',
            }}
          >
            {/* Orange radial ornament (logo orange) */}
            <div
              aria-hidden
              className="absolute -right-14 -top-14 w-40 h-40 rounded-full opacity-30 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, #FF9900, transparent)' }}
            />
            {/* Subtle warmth wash */}
            <div
              className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
              style={{
                background:
                  'radial-gradient(60rem 30rem at 100% 0%, rgba(255,255,255,0.35), transparent 60%)',
              }}
            />
            <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 px-6 py-8 sm:px-14 sm:py-11 text-white">
              <div className="flex items-center gap-3 sm:block sm:flex-shrink-0 sm:self-start">
                <div
                  className="w-11 h-11 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(255,153,0,0.15)',
                    boxShadow: 'inset 0 0 0 1px rgba(255,153,0,0.55)',
                  }}
                >
                  <Sparkles size={22} strokeWidth={2} className="text-[#FF9900] sm:hidden" />
                  <Sparkles size={28} strokeWidth={2} className="text-[#FF9900] hidden sm:block" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  className="text-3xl sm:text-5xl leading-[1.05] tracking-tight mb-2 sm:mb-3"
                  style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
                >
                  <span
                    className="text-[2.5rem] sm:text-[3.75rem] pr-2 align-baseline"
                    style={{
                      fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
                      fontWeight: 500,
                      fontStyle: 'italic',
                      background: 'linear-gradient(135deg, #ffb74d 0%, #FF9900 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      lineHeight: 0.9,
                    }}
                  >
                    New
                  </span>
                  <span className="text-white">Wellness Suite</span>
                </h2>
                <p className="text-white/90 text-lg sm:text-lg leading-snug max-w-xl font-semibold">
                  60-min private session
                </p>
                <p className="text-white/70 text-[15px] sm:text-base leading-snug max-w-xl mt-1">
                  Infrared · Red Light · Massage · Aromatherapy
                </p>
              </div>
              <div className="hidden sm:flex flex-col items-end justify-center">
                <div
                  className="text-5xl leading-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: '#FF9900' }}
                >
                  $69
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] mt-3"
                  style={{ color: '#FF9900' }}
                >
                  Book Now <ArrowRight size={12} strokeWidth={2.5} />
                </span>
              </div>
              {/* Mobile-only price row */}
              <div className="flex sm:hidden items-center justify-between border-t border-white/15 pt-3 mt-2">
                <div
                  className="text-3xl leading-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: '#FF9900' }}
                >
                  $69
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em]"
                  style={{ color: '#FF9900' }}
                >
                  Book Now <ArrowRight size={12} strokeWidth={2.5} />
                </span>
              </div>
            </div>
          </a>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Pre Order tile — CREAM */}
            <button
              data-testid="launcher-preorder"
              onClick={openLogin}
              className="relative overflow-hidden rounded-3xl px-6 py-10 lg:p-10 active:scale-[0.99] transition-all flex flex-col items-center text-center"
              style={{
                minHeight: 220,
                background: 'linear-gradient(135deg, #faf7f0 0%, #e8dfc7 100%)',
                boxShadow:
                  '0 14px 40px rgba(31,61,43,0.10), inset 0 0 0 1px rgba(14,77,42,0.12), inset 0 1px 0 rgba(255,255,255,0.6)',
              }}
            >
              {/* Green corner ornament */}
              <div
                aria-hidden
                className="absolute -right-14 -top-14 w-40 h-40 rounded-full opacity-15 pointer-events-none"
                style={{ background: 'radial-gradient(closest-side, #0e4d2a, transparent)' }}
              />
              {/* Green-ringed icon */}
              <div
                className="relative w-14 h-14 rounded-full flex items-center justify-center mb-5 flex-shrink-0"
                style={{
                  background: 'rgba(14,77,42,0.08)',
                  boxShadow: 'inset 0 0 0 1px rgba(14,77,42,0.25)',
                }}
              >
                <Boxes className="text-[#0e4d2a]" size={24} strokeWidth={2} />
              </div>
              <h2
                className="relative text-xl mb-2 leading-tight tracking-tight text-[#0d1f15]"
                style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
              >
                Pre Order
              </h2>
              <p
                className="relative text-sm text-[#5a4e2e] mb-5 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Staff app for preorders, pickups &amp; incoming
              </p>
              <span
                className="relative inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] mt-auto"
                style={{ color: '#c26319' }}
              >
                <Lock size={11} strokeWidth={2.5} /> Staff sign in
              </span>
            </button>

            {/* Wellness Club Kiosk tile — LOGO INK (near-black with green undertone) */}
            <a
              data-testid="launcher-kiosk"
              href="/kiosk"
              className="relative overflow-hidden rounded-3xl px-6 py-10 lg:p-10 active:scale-[0.99] transition-all flex flex-col items-center text-center no-underline text-white"
              style={{
                minHeight: 220,
                background: 'linear-gradient(135deg, #1A1C14 0%, #2b3323 100%)',
                boxShadow: '0 14px 40px rgba(26,28,20,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              {/* Orange corner ornament (logo orange) */}
              <div
                aria-hidden
                className="absolute -right-14 -top-14 w-40 h-40 rounded-full opacity-25 pointer-events-none"
                style={{ background: 'radial-gradient(closest-side, #FF9900, transparent)' }}
              />
              {/* Cream warmth wash */}
              <div
                className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none"
                style={{
                  background:
                    'radial-gradient(60rem 30rem at 100% 0%, rgba(255,255,255,0.35), transparent 60%)',
                }}
              />
              {/* Orange-ringed icon */}
              <div
                className="relative w-14 h-14 rounded-full flex items-center justify-center mb-5 flex-shrink-0"
                style={{
                  background: 'rgba(255,153,0,0.12)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,153,0,0.55)',
                }}
              >
                <Sparkles size={24} strokeWidth={2} className="text-[#FF9900]" />
              </div>
              <h2
                className="relative text-xl mb-2 leading-tight tracking-tight text-white"
                style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
              >
                Wellness Club Kiosk
              </h2>
              <p
                className="relative text-sm text-white/75 mb-5 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Personalized vitamin plan in 90 seconds
              </p>
              <span
                className="relative inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] mt-auto"
                style={{ color: '#FF9900' }}
              >
                Open kiosk <ArrowRight size={11} strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </div>
      )}

      {mode === 'login' && (
        <div className="w-full max-w-sm bg-white border border-[#1f3d2b]/10 rounded-3xl p-8 shadow-sm">
          <button
            data-testid="login-back-button"
            onClick={() => {
              setMode('choose');
              setPasscode('');
              setError('');
            }}
            className="text-sm text-[#5a7062] hover:text-[#0d1f15] inline-flex items-center gap-1 mb-5 font-semibold"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center mb-3">
              <Lock className="text-[#1f3d2b]" size={24} />
            </div>
            <h1
              className="text-2xl text-[#0d1f15] tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
            >
              Pre Order Sign In
            </h1>
            <p className="text-sm text-[#5a7062] mt-1">Enter the store passcode</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#5a7062] mb-2">
                Passcode
              </label>
              <input
                data-testid="login-passcode-input"
                type="password"
                inputMode="numeric"
                autoFocus
                value={passcode}
                onChange={(e) => {
                  setPasscode(e.target.value);
                  if (error) setError('');
                }}
                placeholder="•••••••••"
                className="w-full h-12 px-4 text-lg tracking-widest text-center border border-[#1f3d2b]/20 rounded-xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/20 transition"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy || !passcode}
              className="w-full bg-[#1f3d2b] hover:bg-[#172d20] text-white font-bold rounded-xl h-12 text-base transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
